import {type ApolloClient, type NormalizedCacheObject, ApolloLink} from '@apollo/client';
import i18next from 'i18next';

import store from 'web-app/react/redux/store';
import APIHelper from 'web-app/services/api-helper';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as SentryService from 'web-app/services/sentry';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as SessionHelpers from 'web-app/react/session/helpers';
import {isBadNetworkError} from 'web-app/util/url';
import {addErrorToErrorLog} from 'web-app/react/routes/error-log/helpers';
import {isE2e} from '@famly/mf_ts-utils_is-e2e';

import apolloClients, {isServerParseError, type NetworkError} from './apollo-client';
import {APIErrorCode} from '../types';

export const getAccessToken = () => store.getState().session.accessToken;

const getStatusType = (severity: string) => {
    switch (severity) {
        case 'Info':
            return 'Info';
        case 'Error':
        default:
            return 'Error';
    }
};

const extractErrorCodeFromGraphqlError = (error: {extensions?: {errorCode?: number}}) => {
    return error.extensions?.errorCode;
};

const errorIsIgnored = (
    error: {extensions?: {errorCode?: number}},
    ignoredErrorCodes: typeof APIErrorCode[keyof typeof APIErrorCode][],
) => {
    const errorCode = extractErrorCodeFromGraphqlError(error);
    return errorCode ? ignoredErrorCodes.includes(errorCode) : false;
};

type ErrorType = 'network-error' | 'graphql-error';

export const getApolloClient = (overridingApiKey?: string): ApolloClient<NormalizedCacheObject> => {
    const tracingMiddleware = new ApolloLink((operation, forward) => {
        return forward(operation);
    });

    return apolloClients(
        APIHelper,
        (error, extra, overrideMessage, context, errorType) => {
            if (isE2e()) {
                console.error('Graphql error while running E2E test');
                console.error('Operation name:', extra?.name ?? "** Couldn't extract operation name **");
                console.error(error);
            }

            if (
                hasValue(context.ignoredErrorCodes) &&
                errorType === 'graphql-error' &&
                errorIsIgnored(error, context.ignoredErrorCodes)
            ) {
                return;
            }

            if (error) {
                if (errorType === 'network-error') {
                    addErrorToErrorLog({
                        type: 'network-error',
                        name: error.name,
                        message: error.message,
                    });
                } else if (errorType === 'graphql-error') {
                    const errorCode = extractErrorCodeFromGraphqlError(error);

                    addErrorToErrorLog({
                        type: 'graphql-error',
                        code: `${errorCode}`,
                        name: extra?.name ?? "Couldn't extract operation name",
                        message: error.message,
                        debuggingInfo: error.extensions?.debug as string | undefined,
                    });
                }
            }

            if (error && !context.ignoreErrorNotification) {
                const severity = errorType === 'graphql-error' && error.extensions?.severity;
                const statusType = getStatusType(severity as string);

                const blocking = errorType === 'graphql-error' && error.extensions?.display === 'Blocking';
                const sticky = errorType === 'graphql-error' && error.extensions?.display === 'Sticky';

                GlobalEventActions.updateStatus.dispatch(
                    overrideMessage || formatErrorMessage(error.message, errorType, error),
                    statusType,
                    blocking || sticky,
                );
            }

            if (extra && extra.log) {
                const extensions = errorType === 'graphql-error' ? error.extensions : {};
                const errorCode = errorType === 'graphql-error' ? extractErrorCodeFromGraphqlError(error) : undefined;
                const message = error ? `${extra.name}: ${error.message}` : extra.name;

                SentryService.sendEvent(message, {
                    extra: {
                        ...extra,
                        ...error,
                        ...extensions,
                    },
                    fingerprint: [
                        `error-type:${errorType}`,
                        `operation-name:${extra.name}`,
                        hasValue(errorCode) ? `error-code:${errorCode}` : undefined,
                    ].filter(hasValue),
                    tags: errorCode ? {errorCode: errorCode.toString()} : undefined,
                });
            }

            if (hasValue(extra) && extra.errorCode === APIErrorCode.SESSION_EXPIRED) {
                // Your login session has expired
                SessionHelpers.logOut(undefined, false);
            }
        },
        overridingApiKey,
        tracingMiddleware,
    );
};

const formatErrorMessage = (message: string | undefined, errorType: ErrorType, error: NetworkError) => {
    if (isServerParseError(error)) {
        // If the backend respons with a non-JSON response, we show a better-looking error message.
        // Otherwise error message would be something like "Unexpected token < at position 0"
        return i18next.t('network.specialCaseErrors.nonJSONResponse');
    }

    const defaultMessage = message || i18next.t('somethingWentWrong');

    switch (errorType) {
        case 'network-error':
            if (error && isBadNetworkError(error)) {
                return i18next.t('network.specialCaseErrors.badNetworkError');
            }
            return defaultMessage;
        case 'graphql-error':
        default:
            return defaultMessage;
    }
};
